import React, { useState } from 'react';
import PageContainer from 'containers/PageContainer';
import { AddressesView } from 'views';
import { useRouteMatch, useHistory } from 'react-router';
import AddAddressView from 'views/AddAddressView';
import AddressSetView from 'views/AddressSetView';
import AddressDeleteConfirmView from 'views/AddressDeleteConfirmView';
import AddressSetConfirmView from 'views/AddressSetConfirmView';
import moment from 'moment';
import queryString from 'query-string';
import { findNextAndPrevPack } from 'helpers/subscriptionHelpers';
import dtHelper from 'helpers/dateTimeHelper';
import useSaveAddress from 'hooks/useSaveAddress';
import useRemoveAddress from 'hooks/useRemoveAddress';
import {
  COUNTRY_BFPO,
  COUNTRY_UK, SAVE_ADDRESS_GUERNSEY_COUNTRY_ERROR,
  SAVE_ADDRESS_JERSEY_COUNTRY_ERROR,
  SAVE_ADDRESS_UK_COUNTRY_ERROR,
} from '../helpers/copyHelper';

const AddressesContainer = () => {
  const match = useRouteMatch();
  const history = useHistory();
  const search = queryString.parse(history.location.search);
  const { action = 'list', addressId } = match.params;
  const today = moment(dtHelper.now()).format('YYYY-MM-DD');

  const [addAddressViewErrors, setAddAddressViewErrors] = useState(null);
  const saveAddress = useSaveAddress();
  const removeAddress = useRemoveAddress();

  React.useEffect(() => {
    setAddAddressViewErrors(null);
  }, [action]);

  const render = (subscription, account, store) => {
    const address = addressId && account.address_book.find((a) => a.id === addressId);
    const { nextDeliverablePack } = findNextAndPrevPack(subscription.packs, today);
    const changeDelayed = nextDeliverablePack && nextDeliverablePack.status !== 'Pending';

    if (subscription.isNonLensMail || account.isMinor) {
      history.replace('/overview');
    }

    const sortedAddresses = [
      account.address_book.find((a) => a.is_default),
      ...account.address_book.filter((a) => !a.is_default),
    ];

    function handleSaveAddress(addressToSave, makePrimary = false, applyToAll = false) {
      saveAddress({
        address: addressToSave,
        subscription,
        onSuccess: history.goBack,
        applyToAll,
        makePrimary,
      });
    }

    function handleRemoveAddress(addressToRemove) {
      removeAddress({
        address: addressToRemove,
        onSuccess: history.goBack,
      });
    }

    function handleAddAddress(addressToSave, makePrimary = false, applyToAll = false) {
      if (
        addressToSave.country === COUNTRY_BFPO
        && !addressToSave.postcode.match(/[A-Z]{2}[0-9]{1,2}\s?[0-9][A-Z]{2}/g)
      ) {
        setAddAddressViewErrors({
          postcode: 'Invalid Postcode',
        });
        return;
      }

      if (
        store.tax_code === 'JEH'
        && addressToSave.country === COUNTRY_UK
        && addressToSave.postcode.indexOf('JE') !== 0
      ) {
        setAddAddressViewErrors({
          country: SAVE_ADDRESS_JERSEY_COUNTRY_ERROR,
        });
        return;
      } else if (
        store.tax_code === 'GYH'
        && addressToSave.country === COUNTRY_UK
        && addressToSave.postcode.indexOf('GY') !== 0
      ) {
        setAddAddressViewErrors({
          country: SAVE_ADDRESS_GUERNSEY_COUNTRY_ERROR,
        });
        return;
      } else if (
        store.tax_code === 'GBH'
        && addressToSave.country === COUNTRY_UK
        && (
          addressToSave.postcode.indexOf('GY') === 0
          || addressToSave.postcode.indexOf('JE') === 0
        )
      ) {
        setAddAddressViewErrors({
          country: SAVE_ADDRESS_UK_COUNTRY_ERROR,
        });
        return;
      }

      setAddAddressViewErrors(null);
      handleSaveAddress(addressToSave, makePrimary, applyToAll);
    }

    const contentMap = {
      list: () => <AddressesView addresses={sortedAddresses} />,
      add: () => (
        <AddAddressView
          onSubmit={handleAddAddress}
          onCancel={() => history.goBack()}
          allowDefaultAddress={!!search.allowDefaultAddress}
          allowAllFutureChange={!!search.allowAllFutureChange}
          changeDelayed={
            changeDelayed
          }
          errors={addAddressViewErrors}
          onChange={(updates) => {
            const errors = { ...addAddressViewErrors };
            Object.keys(updates).forEach((x) => delete errors[x]);
            setAddAddressViewErrors(errors);
          }}
          userStoreTaxRegion={store.tax_code}
        />
      ),
      set: () => (
        <AddressSetConfirmView
          onSubmit={handleSaveAddress}
          onCancel={() => history.goBack()}
          address={address}
          forceMakePrimary={address.is_default}
          changeDelayed={
            changeDelayed
          }
        />
      ),
      changeDefault: () => (
        <AddressSetView
          onSubmit={handleSaveAddress}
          currentAddressId={
            account && account.address_book.find((a) => a.is_default).id
          }
          addresses={account && account.address_book}
          enableApplyToAll
          forceApplyToAll
          enablePrimary
        />
      ),
      delete: () => (
        <AddressDeleteConfirmView
          onCancel={() => history.goBack()}
          address={address}
          onDelete={() => handleRemoveAddress(address)}
        />
      ),
    };

    return contentMap[action]();
  };

  return (
    <PageContainer
      logoRef="/overview"
      requiresAccount
      requiresLogin
      displayNavBar={action === 'list'}
      requiresSubscription
      hasBack={action !== 'list'}
      render={render}
    />
  );
};

AddressesContainer.displayName = 'AddressesContainer';

export default AddressesContainer;
